import { createGtm } from '@gtm-support/vue-gtm'
import type { RouteRecordRaw } from 'vue-router'
import { createRouter, createWebHistory } from 'vue-router'

import eventSender from '@last/core-ui/scripts/eventSender'

import Home from '@/components/Home.vue'

import { app } from './main'
import { useAuthStore } from './store/auth'
import { useConfigStore } from './store/config'
import { pinia } from './store/pinia'
import { useReservationStore } from './store/reservation'

const routes: RouteRecordRaw[] = [
  {
    name: 'home',
    path: '/:organizationId?/:locationId/booking',
    component: Home,
    beforeEnter: async (to, _from, next) => {
      const authStore = useAuthStore(pinia)
      const configStore = useConfigStore(pinia)
      const bookingStore = useReservationStore(pinia)

      authStore.setLocationId(to.params.locationId as string)

      const bookingId = to.params?.bookingId
      if (bookingId) {
        bookingStore.setBookingId(bookingId as string)
      }

      const organizationId = to.params?.organizationId
      if (organizationId) {
        authStore.setOrganizationId(organizationId as string)
      }

      await Promise.all([
        authStore.getOrganizationLocations(),
        configStore.getLocationConfig(),
        bookingStore.getBookingInfo()
      ])

      if (configStore.config.scripts?.googleTagManagerId) {
        app.use(
          createGtm({
            id: configStore.config.scripts.googleTagManagerId
          })
        )
      }

      await eventSender.load(configStore.config.scripts, router)

      next()
    },
    redirect: { name: 'booking' },
    children: [
      {
        name: 'booking',
        path: ':bookingId?',
        component: () => import('@/components/Booking.vue')
      },
      {
        name: 'zoneSelection',
        path: ':bookingId?/zone',
        component: () => import('@/components/ZoneSelection.vue')
      },
      {
        name: 'customerInfo',
        path: ':bookingId?/customerInfo',
        component: () => import('@/components/CustomerInfo.vue')
      },
      {
        name: 'warranty',
        path: ':bookingId?/warranty',
        component: () => import('@/components/Warranty.vue')
      },
      {
        name: 'done',
        path: ':bookingId/done',
        redirect: { name: 'summary' }
      },
      {
        name: 'summary',
        path: ':bookingId/summary',
        component: () => import('@/components/Summary.vue')
      },
      {
        name: 'cancel',
        path: ':bookingId/cancel',
        component: () => import('@/components/Cancel.vue')
      },
      {
        name: 'cancelled',
        path: ':bookingId/cancelled',
        component: () => import('@/components/Cancel.vue'),
        props: { cancelled: true }
      }
    ]
  },
  {
    name: 'termsAndConditions',
    path: '/termsAndConditions',
    component: () => import('@/components/TermsAndConditions.vue')
  },
  {
    name: '404',
    path: '/:pathMatch(.*)*',
    component: () => import('@/components/404.vue')
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

export default router
